import api from "../hooks/api";
import { API_URLS } from "../App";
import {
  CampaignSummary,
  Newsletter,
  NewsletterBlock,
  NewsletterRecipient,
  NewsletterStats,
  NewsletterTemplate,
} from "../model/NewsletterCampaign";

const base = () => `${API_URLS.NEWSLETTER_ADMIN}/admin`;

export interface NewsletterInput {
  name: string;
  subject: string;
  template_id?: string | null;
  blocks: NewsletterBlock[];
  group_ids: string[];
  tracking_enabled: boolean;
}

export interface TemplateInput {
  name: string;
  blocks: NewsletterBlock[];
  html_source?: string;
}

export async function listNewsletterTemplates(): Promise<NewsletterTemplate[]> {
  const res = await api.get(`${base()}/templates`);
  return res.data?.data || [];
}

export async function createNewsletterTemplate(
  input: TemplateInput,
): Promise<NewsletterTemplate> {
  const res = await api.post(`${base()}/templates`, input);
  return res.data;
}

export async function updateNewsletterTemplate(
  id: string,
  input: TemplateInput,
): Promise<NewsletterTemplate> {
  const res = await api.put(`${base()}/templates/${id}`, input);
  return res.data;
}

export async function deleteNewsletterTemplate(id: string): Promise<void> {
  await api.delete(`${base()}/templates/${id}`);
}

export async function listNewsletters(params?: {
  status?: string;
  page?: number;
  perPage?: number;
}): Promise<{ data: Newsletter[]; total: number }> {
  const res = await api.get(`${base()}/newsletters`, {
    params: {
      status: params?.status || undefined,
      page: params?.page || 1,
      per_page: params?.perPage || 25,
    },
  });
  return { data: res.data?.data || [], total: res.data?.total || 0 };
}

export async function createNewsletter(
  input: NewsletterInput,
): Promise<Newsletter> {
  const res = await api.post(`${base()}/newsletters`, input);
  return res.data;
}

export async function updateNewsletter(
  id: string,
  input: NewsletterInput,
): Promise<Newsletter> {
  const res = await api.put(`${base()}/newsletters/${id}`, input);
  return res.data;
}

export async function deleteNewsletter(id: string): Promise<void> {
  await api.delete(`${base()}/newsletters/${id}`);
}

export async function previewNewsletter(id: string): Promise<string> {
  const res = await api.post(`${base()}/newsletters/${id}/preview`);
  return res.data?.html || "";
}

export async function scheduleNewsletter(
  id: string,
  scheduledAt: string,
): Promise<Newsletter> {
  const res = await api.post(`${base()}/newsletters/${id}/schedule`, {
    scheduled_at: scheduledAt,
  });
  return res.data;
}

export async function sendNewsletterNow(id: string): Promise<Newsletter> {
  const res = await api.post(`${base()}/newsletters/${id}/send`);
  return res.data;
}

export async function cancelNewsletter(id: string): Promise<Newsletter> {
  const res = await api.post(`${base()}/newsletters/${id}/cancel`);
  return res.data;
}

export async function retryFailedNewsletter(id: string): Promise<Newsletter> {
  const res = await api.post(`${base()}/newsletters/${id}/retry-failed`);
  return res.data;
}

export function newsletterErrorMessage(err: any): string {
  const status = err?.response?.status;
  const raw = err?.response?.data?.error;
  if (status === 400)
    return "Dados inválidos. Verifica os campos obrigatórios e a lista de destinatários.";
  if (status === 409)
    return "Esta ação não é permitida no estado atual da newsletter.";
  if (status === 503)
    return "O serviço de campanhas está indisponível. Tenta novamente.";
  return raw || "Ocorreu um erro. Tente novamente.";
}

export async function getNewsletterStats(id: string): Promise<NewsletterStats> {
  const res = await api.get(`${base()}/newsletters/${id}/stats`);
  return res.data;
}

export async function listNewsletterRecipients(
  id: string,
): Promise<{ data: NewsletterRecipient[]; total: number }> {
  const res = await api.get(`${base()}/newsletters/${id}/recipients`, {
    params: { page: 1, per_page: 100 },
  });
  return { data: res.data?.data || [], total: res.data?.total || 0 };
}

export async function listNiposomCampaigns(): Promise<CampaignSummary[]> {
  const res = await api.get(`${base()}/niposom/campaigns`);
  return res.data?.data || [];
}

export async function uploadNewsletterImage(file: File): Promise<string> {
  const formData = new FormData();
  formData.append("file", file);
  const res = await api.post("/newsletter-media/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data?.url || "";
}

export async function listSubscriberMetadataKeys(): Promise<string[]> {
  const res = await api.get(`${base()}/metadata-keys`);
  return res.data?.data || [];
}

export async function listNewsletterGroups() {
  const res = await api.get(`${base()}/groups`);
  return res.data?.data || [];
}
